import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { tableReservationsApi } from "../../../api/tableReservations";

const STATUS_OPTIONS = [
  ["pending", "Pendiente"],
  ["confirmed", "Confirmada"],
  ["completed", "Completada"],
  ["cancelled", "Cancelada"],
  ["no_show", "No asistió"],
];

const emptyForm = () => ({
  fullName: "",
  phone: "",
  email: "",
  startAt: "",
  partySize: 2,
  durationMinutes: 90,
  status: "confirmed",
  tableIds: [],
  notes: "",
});

function toLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function dateTimeLabel(value, timezone) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: timezone || undefined,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function statusLabel(status) {
  return STATUS_OPTIONS.find(([value]) => value === status)?.[1] || status;
}

function tableLabel(table) {
  if (!table) return "Sin mesa asignada";
  const name = table.name ? ` · ${table.name}` : "";
  const sector = table.sector ? ` · ${table.sector}` : "";
  return `Mesa ${table.identifier}${name}${sector}`;
}

function reservationTables(reservation) {
  if (Array.isArray(reservation?.tableSnapshots) && reservation.tableSnapshots.length) {
    return reservation.tableSnapshots;
  }
  return reservation?.tableSnapshot ? [reservation.tableSnapshot] : [];
}

function assignmentLabel(tables) {
  if (!tables?.length) return "Sin mesa asignada";
  return tables.map((table) => `Mesa ${table.identifier}${table.name ? ` (${table.name})` : ""}`).join(" + ");
}

function sectorsLabel(tables) {
  const sectors = Array.from(new Set((tables || []).map((table) => table.sector).filter(Boolean)));
  return sectors.length ? sectors.join(" / ") : "Sin sector";
}

export default function TableReservationsPanel({ projectId }) {
  const [reservations, setReservations] = React.useState([]);
  const [availability, setAvailability] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const timezone = React.useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Argentina/Mendoza",
    [],
  );

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const rows = await tableReservationsApi.list(projectId);
      setReservations(rows);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las reservas gastronómicas.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const setField = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(["startAt", "partySize", "durationMinutes"].includes(field) ? { tableIds: [] } : {}),
    }));
    if (["startAt", "partySize", "durationMinutes"].includes(field)) setAvailability(null);
    setMessage("");
  };

  const setSelectedTables = (event) => {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      tableIds: Array.isArray(value) ? value : String(value || "").split(",").filter(Boolean),
    }));
    setError("");
    setMessage("");
  };

  const checkAvailability = async () => {
    if (!form.startAt) {
      setError("Elegí fecha y hora antes de buscar una mesa.");
      return;
    }
    try {
      setChecking(true);
      setError("");
      const data = await tableReservationsApi.availability(projectId, {
        startAt: new Date(form.startAt).toISOString(),
        durationMinutes: Number(form.durationMinutes),
        partySize: Number(form.partySize),
        excludeReservationId: editingId || undefined,
      });
      setAvailability(data);
      if (data?.calendarBlocked) {
        setError("Ese horario está bloqueado en la agenda general.");
      }
    } catch (err) {
      setError(err.message || "No se pudo consultar la disponibilidad.");
    } finally {
      setChecking(false);
    }
  };

  const reset = () => {
    setForm(emptyForm());
    setEditingId("");
    setAvailability(null);
    setError("");
    setMessage("");
  };

  const edit = (reservation) => {
    const ids = Array.isArray(reservation.tableIds) && reservation.tableIds.length
      ? reservation.tableIds
      : reservation.tableId
        ? [reservation.tableId]
        : [];
    setEditingId(reservation.id);
    setForm({
      fullName: reservation.customer?.fullName || "",
      phone: reservation.customer?.phone || "",
      email: reservation.customer?.email || "",
      startAt: toLocalInput(reservation.startAt),
      partySize: Number(reservation.partySize || 1),
      durationMinutes: Number(reservation.durationMinutes || 90),
      status: reservation.status || "confirmed",
      tableIds: ids,
      notes: reservation.notes || "",
    });
    setAvailability(null);
    setError("");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyAlternative = (alternative) => {
    setForm((current) => ({ ...current, tableIds: alternative.tableIds || [] }));
    setError("");
    setMessage(`Asignación sugerida aplicada: ${assignmentLabel(alternative.tables)}.`);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.fullName.trim() || !form.phone.trim() || !form.startAt) {
      setError("Completá cliente, teléfono, fecha y hora.");
      return;
    }
    if (availability && form.tableIds.length && selectedCapacity < Number(form.partySize || 0)) {
      setError(`Las mesas seleccionadas suman ${selectedCapacity} lugares y la reserva es para ${Number(form.partySize || 0)} personas.`);
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        customer: {
          fullName: form.fullName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
        },
        startAt: new Date(form.startAt).toISOString(),
        partySize: Number(form.partySize),
        durationMinutes: Number(form.durationMinutes),
        status: form.status,
        tableIds: form.tableIds,
        notes: form.notes.trim(),
        timezone,
      };
      if (editingId) {
        await tableReservationsApi.update(projectId, editingId, payload);
        setMessage("Reserva gastronómica actualizada.");
      } else {
        await tableReservationsApi.create(projectId, payload);
        setMessage("Reserva gastronómica creada.");
      }
      setForm(emptyForm());
      setEditingId("");
      setAvailability(null);
      await load();
    } catch (err) {
      setError(err.message || "No se pudo guardar la reserva.");
    } finally {
      setSaving(false);
    }
  };

  const availabilityTables = availability?.tables || [];
  const alternatives = availability?.alternatives || [];
  const occupancy = availability?.occupancy;
  const selectableTables = availabilityTables.filter((table) => table.available);
  const selectedCapacity = availabilityTables
    .filter((table) => form.tableIds.includes(table.id))
    .reduce((sum, table) => sum + Number(table.capacity || 0), 0);

  return (
    <Stack spacing={3}>
      <Alert severity="info" icon={<EventSeatRoundedIcon />}>
        Esta sección contiene sólo reservas gastronómicas. Los turnos de servicios siguen usando la agenda de servicios y no se mezclan en esta vista.
      </Alert>

      <Card component="form" onSubmit={submit} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack spacing={2.2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              {editingId ? "Editar reserva de mesa" : "Nueva reserva de mesa"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>
              La asignación es opcional. Podés elegir una o varias mesas manualmente, usar una sugerencia o dejar la reserva pendiente de mesa.
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 1fr" }, gap: 1.5 }}>
            <TextField label="Cliente" value={form.fullName} onChange={setField("fullName")} required />
            <TextField label="Teléfono" value={form.phone} onChange={setField("phone")} required />
            <TextField label="Email" value={form.email} onChange={setField("email")} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1.5fr .7fr .8fr .9fr" }, gap: 1.5 }}>
            <TextField
              label="Fecha y hora"
              type="datetime-local"
              value={form.startAt}
              onChange={setField("startAt")}
              InputLabelProps={{ shrink: true }}
              required
              sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
            />
            <TextField label="Personas" type="number" inputProps={{ min: 1, max: 1000 }} value={form.partySize} onChange={setField("partySize")} required />
            <TextField label="Duración (min)" type="number" inputProps={{ min: 15, max: 1440, step: 15 }} value={form.durationMinutes} onChange={setField("durationMinutes")} required />
            <TextField select label="Estado" value={form.status} onChange={setField("status")}>
              {STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }}>
            <Button variant="outlined" onClick={checkAvailability} disabled={checking || saving}>
              {checking ? "Buscando…" : "Revisar disponibilidad"}
            </Button>
            {form.tableIds.length ? (
              <Button variant="text" onClick={() => setForm((current) => ({ ...current, tableIds: [] }))} disabled={saving}>
                Dejar sin mesa
              </Button>
            ) : null}
            {availability ? (
              <Typography variant="body2" color="text.secondary">
                {selectableTables.length} de {availabilityTables.length} mesa(s) libres en esa franja.
              </Typography>
            ) : null}
          </Stack>

          {occupancy ? (
            <Alert severity={occupancy.expectedGuests > occupancy.totalCapacity ? "warning" : "info"}>
              Ocupación esperada: <strong>{occupancy.expectedGuests} personas</strong> incluyendo esta reserva. Actualmente hay {occupancy.currentGuests} personas previstas, {occupancy.occupiedTables} de {occupancy.totalTables} mesas comprometidas y {occupancy.unassignedReservations} reserva(s) todavía sin mesa.
            </Alert>
          ) : null}

          {availability ? (
            <TextField
              select
              label="Mesas asignadas (opcional)"
              value={form.tableIds}
              onChange={setSelectedTables}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const selectedTables = availabilityTables.filter((table) => selected.includes(table.id));
                  return assignmentLabel(selectedTables);
                },
              }}
              helperText={form.tableIds.length
                ? `Capacidad asignada: ${selectedCapacity} para ${form.partySize} personas.`
                : "Sin selección, la reserva se guarda pendiente de asignación."}
            >
              {availabilityTables.map((table) => (
                <MenuItem key={table.id} value={table.id} disabled={!table.available}>
                  {tableLabel(table)} · {table.capacity} lugares{table.available ? "" : " · ocupada en ese horario"}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {availability && alternatives.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Asignación asistida</Typography>
              <Stack spacing={1}>
                {alternatives.slice(0, 5).map((alternative, index) => (
                  <Card key={alternative.tableIds.join(":")} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {index === 0 ? "Mejor opción · " : "Alternativa · "}{assignmentLabel(alternative.tables)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {alternative.capacity} lugares · {sectorsLabel(alternative.tables)}{alternative.combined ? " · mesas combinadas" : ""}
                        </Typography>
                      </Box>
                      <Button size="small" variant={index === 0 ? "contained" : "outlined"} onClick={() => applyAlternative(alternative)}>
                        Usar
                      </Button>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </Box>
          ) : null}

          {availability && !availability.calendarBlocked && !alternatives.length ? (
            <Alert severity="warning">
              No hay una mesa o combinación con capacidad suficiente libre en esa franja. Podés mantener la reserva sin mesa asignada y resolverla después.
            </Alert>
          ) : null}

          {availability?.conflicts?.some((item) => !item.unassigned) ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Conflictos de ocupación</Typography>
              <Stack spacing={.8}>
                {availability.conflicts.filter((item) => !item.unassigned).map((conflict) => (
                  <Alert key={conflict.reservationId} severity="warning">
                    {assignmentLabel(conflict.tables)} · {conflict.customerName || conflict.appointmentNumber || "Reserva existente"} · {dateTimeLabel(conflict.startAt, timezone)} a {dateTimeLabel(conflict.endAt, timezone)}.
                  </Alert>
                ))}
              </Stack>
            </Box>
          ) : null}

          <TextField label="Notas" multiline minRows={2} value={form.notes} onChange={setField("notes")} />
          {error ? <Alert severity="error">{error}</Alert> : null}
          {message ? <Alert severity="success">{message}</Alert> : null}

          <Stack direction="row" spacing={1.2} justifyContent="flex-end">
            {editingId ? <Button onClick={reset} disabled={saving}>Cancelar edición</Button> : null}
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear reserva"}
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Box>
        <Typography variant="h5" sx={{ fontWeight: 900, mb: 1.5 }}>Reservas gastronómicas</Typography>
        {loading ? <Box sx={{ py: 5, textAlign: "center" }}><CircularProgress /></Box> : null}
        {!loading && reservations.length === 0 ? (
          <Alert severity="info">Todavía no hay reservas de mesa.</Alert>
        ) : null}
        <Stack spacing={1.2}>
          {reservations.map((reservation) => {
            const tables = reservationTables(reservation);
            const capacity = Number(reservation.assignedCapacity || tables.reduce((sum, table) => sum + Number(table.capacity || 0), 0));
            return (
              <Card key={reservation.id} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 900 }}>{reservation.customer?.fullName || "Cliente"}</Typography>
                      <Chip size="small" label={statusLabel(reservation.status)} />
                      {!tables.length ? <Chip size="small" variant="outlined" label="Sin mesa" /> : null}
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .4, sm: 2 }} sx={{ mt: .8 }}>
                      <Typography variant="body2" color="text.secondary"><AccessTimeRoundedIcon sx={{ fontSize: 16, mr: .5, verticalAlign: "text-bottom" }} />{dateTimeLabel(reservation.startAt, reservation.timezone)}</Typography>
                      <Typography variant="body2" color="text.secondary"><PeopleAltRoundedIcon sx={{ fontSize: 16, mr: .5, verticalAlign: "text-bottom" }} />{reservation.partySize} personas · {reservation.durationMinutes} min</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: .6 }}>
                      {assignmentLabel(tables)}{tables.length ? ` · ${capacity} lugares · ${sectorsLabel(tables)}` : ""}
                    </Typography>
                    {reservation.notes ? <><Divider sx={{ my: 1 }} /><Typography variant="body2" color="text.secondary">{reservation.notes}</Typography></> : null}
                  </Box>
                  <Button variant="outlined" onClick={() => edit(reservation)}>Editar</Button>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}
